import { revenueCenterApi } from '@/api/restaurant/revenueCenterApi';
import type { Channel, RevenueCenter, SetEnabled } from '@/models/restaurant/RevenueCenter';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from '../../theme';
import SaveFooter from '../common/SaveFooter';
import { useToast } from '../common/toast/ToastProvider';

// Revenue centers and channels are owned by the POS. The only thing editable here is whether TouchTab
// offers them, so both grids are read-only apart from the Enabled switch.
//
// Edits are held locally and committed by the footer, so the page keeps a map of id -> pending value rather
// than mutating the query data. Only the rows the operator actually changed are sent; an untouched grid
// saves nothing.
const FORM_ID = 'revenue-centers-form';

// Fixed height for the two side grids. autoPageSize then fits as many rows as this leaves room for, so the
// page size follows the height instead of the height following the row count.
const SIDE_GRID_HEIGHT = 480;

const RevenueCenters = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const canUpdate = usePermissions().canUpdate(Perm.configurationRevenueCenters);

    const locationId = app.locationId ?? undefined;

    const [tab, setTab] = useState(0);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [pendingRcs, setPendingRcs] = useState<Record<string, boolean>>({});
    const [pendingChannels, setPendingChannels] = useState<Record<string, boolean>>({});

    const revenueCentersKey = ['revenue-centers', locationId ?? ''];
    const channelsKey = ['revenue-center-channels', locationId ?? ''];

    // Refetch on every visit. The POS pushes these on its own schedule, so a cached list can be wrong
    // without anything happening in the portal to invalidate it. isFetching (not isPending) drives the grid
    // overlay: on a revisit the cached rows render immediately and isPending is already false, so using it
    // would leave a refresh completely invisible.
    const { data: revenueCenters, isFetching: isLoadingRcs } = useQuery<RevenueCenter[]>({
        queryKey: revenueCentersKey,
        enabled: !!locationId,
        queryFn: () => revenueCenterApi.list(locationId!),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
    });

    const { data: channels, isFetching: isLoadingChannels } = useQuery<Channel[]>({
        queryKey: channelsKey,
        enabled: !!locationId,
        queryFn: () => revenueCenterApi.listChannels(locationId!),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
    });

    // Changing location swaps out the underlying data entirely, so pending edits from the previous one are
    // meaningless — keeping them would apply one location's toggles to another's rows.
    useEffect(() => {
        setPendingRcs({});
        setPendingChannels({});
        setSelectedId(null);
    }, [locationId]);

    const { mutateAsync: save, isPending: isSaving } = useMutation({
        mutationFn: async () => {
            const rcs: SetEnabled[] = Object.entries(pendingRcs).map(([id, enabled]) => ({ id, enabled }));
            const chs: SetEnabled[] = Object.entries(pendingChannels).map(([id, enabled]) => ({ id, enabled }));

            if (rcs.length > 0) await revenueCenterApi.setEnabled(locationId!, rcs);
            if (chs.length > 0) await revenueCenterApi.setChannelsEnabled(locationId!, chs);
        },
        onSuccess: async () => {
            setPendingRcs({});
            setPendingChannels({});
            toast({ severity: 'success', message: strings.revenueCentersSaved });
            // A revenue center row carries its channels' enabled state too, so a channel save invalidates
            // both lists rather than only the one that was edited.
            await qc.invalidateQueries({ queryKey: revenueCentersKey });
            await qc.invalidateQueries({ queryKey: channelsKey });
        },
        onError: () => toast({ severity: 'error', message: strings.revenueCentersSaveError }),
    });

    // What the grid shows: the pending value where one exists, otherwise what the server last returned.
    const rcRows = useMemo(
        () => (revenueCenters ?? []).map(rc => ({ ...rc, enabled: pendingRcs[rc.id] ?? rc.enabled })),
        [revenueCenters, pendingRcs],
    );

    const channelRows = useMemo(
        () => (channels ?? []).map(c => ({ ...c, enabled: pendingChannels[c.id] ?? c.enabled })),
        [channels, pendingChannels],
    );

    const selected = rcRows.find(rc => rc.id === selectedId) ?? null;

    // The assigned-channel rows carry the server's enabled value, so an unsaved toggle made on the Channels
    // tab has to be applied here too - otherwise the two tabs contradict each other until Save.
    const assignedChannels = useMemo(
        () => (selected?.channels ?? []).map(c => ({ ...c, enabled: pendingChannels[c.id] ?? c.enabled })),
        [selected, pendingChannels],
    );

    const isDirty = Object.keys(pendingRcs).length > 0 || Object.keys(pendingChannels).length > 0;

    // Compares against the value the *server* last returned, not the one currently rendered. That is what
    // makes flipping a switch back to where it started drop the pending edit rather than record a no-op —
    // so an operator who changes their mind leaves the page clean and the Save button disabled.
    const enabledColumn = <T extends { id: string; name: string; enabled: boolean }>(
        serverRows: T[] | undefined,
        setPending: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    ): GridColDef<T> => ({
        field: 'enabled',
        headerName: strings.enabled,
        width: 140,
        renderCell: (params) => (
            <Switch
                checked={params.row.enabled}
                disabled={!canUpdate}
                onChange={(e) => {
                    const enabled = e.target.checked;
                    const original = serverRows?.find(r => r.id === params.row.id)?.enabled ?? false;
                    setPending(prev => {
                        const next = { ...prev };
                        if (enabled === original) delete next[params.row.id];
                        else next[params.row.id] = enabled;
                        return next;
                    });
                }}
                inputProps={{ 'aria-label': `${strings.enabled} ${params.row.name}` }}
            />
        ),
    });

    const rcColumns: GridColDef<RevenueCenter>[] = [
        { field: 'name', headerName: strings.name, flex: 1 },
        enabledColumn<RevenueCenter>(revenueCenters, setPendingRcs),
    ];

    const channelColumns: GridColDef<Channel>[] = [
        { field: 'name', headerName: strings.name, flex: 1 },
        enabledColumn<Channel>(channels, setPendingChannels),
    ];

    // Read-only here on purpose: a channel is enabled for the location, not per revenue center, so the
    // switch that changes it lives on the Channels tab. This column exists so that seeing which channels a
    // revenue center is assigned does not require going and cross-referencing the other tab.
    const assignedChannelColumns: GridColDef[] = [
        { field: 'name', headerName: strings.name, flex: 1 },
        {
            field: 'enabled',
            headerName: strings.enabled,
            width: 110,
            align: 'center',
            headerAlign: 'center',
            // Both states get a mark rather than blank-for-false: a channel missing from this grid and a
            // channel present but switched off mean very different things, and an empty cell reads as
            // "no data" rather than "off".
            renderCell: (p) => (p.row.enabled
                ? <CheckIcon fontSize="small" color="success" titleAccess={strings.enabled} />
                : <CloseIcon fontSize="small" color="disabled" titleAccess={strings.disabled} />),
        },
    ];

    const tableColumns: GridColDef[] = [
        { field: 'number', headerName: strings.revenueCentersTableNumber, width: 110 },
        { field: 'name', headerName: strings.name, flex: 1 },
    ];

    if (!locationId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.revenueCentersSelectLocation}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* SaveFooter's button is a submit for this form — it has no onClick of its own. */}
            <Box
                component="form"
                id={FORM_ID}
                onSubmit={(e: React.FormEvent) => { e.preventDefault(); save(); }}
                sx={{ display: 'none' }}
            />

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                    <Tab label={strings.revenueCenters} />
                    <Tab label={strings.channels} />
                </Tabs>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, p: 2, overflow: 'hidden' }}>
                {tab === 0 ? (
                    <Stack direction="row" spacing={2} sx={{ height: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <DataGrid
                                rows={rcRows}
                                columns={rcColumns}
                                autoPageSize
                                loading={isLoadingRcs}
                                onRowClick={(params) => setSelectedId(params.id.toString())}
                                localeText={{ noRowsLabel: strings.revenueCentersNone }}
                                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                            />
                        </Box>

                        {/* Both side grids are a fixed height and page, rather than growing with their rows.
                            A revenue center can have a lot of tables, and letting the grid size itself pushed
                            the one below it off screen. flexShrink 0 keeps them at that height instead of
                            being squeezed when the pair is taller than the column. */}
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
                            <Box sx={{ flexShrink: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>{strings.channels}</Typography>
                                <Box sx={{ height: SIDE_GRID_HEIGHT }}>
                                    <DataGrid
                                        rows={assignedChannels}
                                        columns={assignedChannelColumns}
                                        autoPageSize
                                        localeText={{
                                            noRowsLabel: selected ? strings.revenueCentersNoChannels : strings.revenueCentersSelectOne,
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>{strings.revenueCentersTables}</Typography>
                                <Box sx={{ height: SIDE_GRID_HEIGHT }}>
                                    <DataGrid
                                        rows={selected?.tables ?? []}
                                        columns={tableColumns}
                                        autoPageSize
                                        localeText={{
                                            noRowsLabel: selected ? strings.revenueCentersNoTables : strings.revenueCentersSelectOne,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Stack>
                ) : (
                    <Box sx={{ height: '100%' }}>
                        <DataGrid
                            rows={channelRows}
                            columns={channelColumns}
                            autoPageSize
                            loading={isLoadingChannels}
                            localeText={{ noRowsLabel: strings.revenueCentersNoChannelsAtAll }}
                        />
                    </Box>
                )}
            </Box>

            {canUpdate && (
                <SaveFooter
                    formId={FORM_ID}
                    formIsDirty={isDirty}
                    saving={isSaving}
                    saveButtonTitle={strings.save}
                    cancelButtonTitle={strings.cancel}
                    onCancel={() => { setPendingRcs({}); setPendingChannels({}); }}
                    // Already reverted in place rather than leaving — see TTD-161.
                    onDiscard={() => { setPendingRcs({}); setPendingChannels({}); }}
                />
            )}
        </Box>
    );
};

export default RevenueCenters;
